import { useEffect, useRef, useState } from "react";
import { parseTakeoffFile } from "../../utils/takeoffFile";
import { parseArchive, looksLikeZip } from "../../utils/takeoffArchive";
import TakeoffArchiveExportDialog from "./TakeoffArchiveExportDialog";
import TakeoffImportDialog from "./TakeoffImportDialog";

// "Takeoff file ▾" — export the revision's takeoff to a file, or import one into this revision. The file holds the
// takeoff (rooms, drawn conditions, edge overrides, each drawing's scale and position) and a list of its drawings;
// it does not carry the drawing files. Export reads from the database (read-only); import replaces this revision's
// takeoff in one transaction after an automatic snapshot, and refuses a locked revision.

function download(filename, data, type = "application/json;charset=utf-8") {
  const blob = new Blob([data], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function TakeoffFileMenu({ disabled, isLocked, sheets, revisionId, roomCount, conditionCount, host, onExport, onImport, onExportArchive = null, onCreateSheets = null }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);   // { file, warnings, fileName, archive } while the import dialog is open
  const [archiveOpen, setArchiveOpen] = useState(false);
  const ref = useRef(null);
  const input = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);

  async function doExport() {
    setOpen(false); setBusy(true); setNote("");
    try { const out = await onExport(); download(out.filename, out.text); setNote(`${out.counts.rooms} rooms, ${out.counts.conditions} conditions → ${out.filename}`); }
    catch (e) { setNote("Export failed: " + (e.message || e)); }
    finally { setBusy(false); }
  }

  async function onPick(e) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setNote("");
    let text, archive = null, extra = [];
    try {
      const looksZip = /\.zip$/i.test(f.name) || f.type === "application/zip" || f.type === "application/x-zip-compressed";
      if (looksZip) {
        const bytes = new Uint8Array(await f.arrayBuffer());
        if (!looksLikeZip(bytes)) { setNote("That is not a zip file."); return; }
        const a = parseArchive(bytes);
        if (!a.ok) { setNote(a.error); return; }
        text = a.takeoffText; archive = a.drawings; extra = a.warnings;
      } else text = await f.text();
    } catch { setNote("Could not read that file."); return; }
    const r = parseTakeoffFile(text);
    if (!r.ok) { setNote(r.error); return; }
    setPending({ file: r.file, warnings: [...extra, ...r.warnings], fileName: f.name, archive });
  }

  async function doExportArchive(selectedIds) {
    const out = await onExportArchive(selectedIds);
    download(out.filename, out.bytes, "application/zip");
    setArchiveOpen(false);
    setNote(`Archive: ${out.counts.rooms} rooms, ${out.counts.conditions} conditions, ${out.counts.files} drawing file${out.counts.files === 1 ? "" : "s"} → ${out.filename}`);
  }

  async function doImport({ document: doc, name }) {
    const result = await onImport({ document: doc, name });
    setPending(null);
    setNote(`Imported: ${result?.RoomsRecreated ?? 0} rooms and ${result?.ConditionsRestored ?? 0} conditions. The previous takeoff is saved in Snapshots.`);
  }

  const item = { display: "block", width: "100%", textAlign: "left", padding: "7px 10px", fontSize: 12.5, background: "transparent", border: "none", cursor: "pointer", color: "var(--text)" };
  return (
    <div ref={ref} style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: 8 }}>
      <button type="button" onClick={() => setOpen(o => !o)} disabled={disabled || busy} aria-haspopup="true" aria-expanded={open}
        title="Export this revision's takeoff to a file, or import one"
        style={{ height: 30, padding: "0 12px", whiteSpace: "nowrap", fontSize: 12, fontWeight: 700, cursor: disabled ? "default" : "pointer", border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", background: "var(--surface)", color: "var(--text2)" }}>
        🗂 Takeoff file ▾
      </button>
      {open && (
        <div role="menu" style={{ position: "absolute", top: 34, right: 0, zIndex: 30, minWidth: 280, background: "var(--surface)", border: "1px solid var(--cs-border)", borderRadius: 8, boxShadow: "0 6px 20px rgba(0,0,0,0.15)", padding: 4 }}>
          <button type="button" role="menuitem" onClick={doExport} style={item}>
            <strong>Export takeoff file…</strong>
            <div style={{ fontSize: 11, color: "var(--text3)" }}>Rooms, drawn conditions, edge overrides and each drawing's scale and position, with a list of its drawings</div>
          </button>
          {onExportArchive && (
            <button type="button" role="menuitem" onClick={() => { setOpen(false); setArchiveOpen(true); }} style={item}>
              <strong>Export project archive (.zip)…</strong>
              <div style={{ fontSize: 11, color: "var(--text3)" }}>The takeoff file plus the drawing files you choose, in one zip</div>
            </button>
          )}
          <button type="button" role="menuitem" onClick={() => { setOpen(false); input.current?.click(); }} disabled={isLocked} style={{ ...item, opacity: isLocked ? 0.5 : 1, cursor: isLocked ? "not-allowed" : "pointer" }}>
            <strong>Import takeoff file or archive…</strong>
            <div style={{ fontSize: 11, color: "var(--text3)" }}>{isLocked ? "This revision is locked" : "Replaces this revision's takeoff (a snapshot of it is saved first)"}</div>
          </button>
        </div>
      )}
      <input ref={input} type="file" accept=".json,.zip,application/json,application/zip" onChange={onPick} style={{ display: "none" }} data-testid="takeoff-file-input" />
      {note && <span role="status" style={{ fontSize: 11, color: "var(--text3)", maxWidth: 320 }}>{note}</span>}
      {archiveOpen && <TakeoffArchiveExportDialog sheets={sheets} onExport={doExportArchive} onCancel={() => setArchiveOpen(false)} />}
      {pending && (
        <TakeoffImportDialog
          file={pending.file} warnings={pending.warnings} fileName={pending.fileName} archive={pending.archive} onCreateSheets={onCreateSheets}
          targetSheets={sheets} targetRevisionId={revisionId} targetRoomCount={roomCount} targetConditionCount={conditionCount}
          isLocked={isLocked} host={host} onImport={doImport} onCancel={() => setPending(null)}
        />
      )}
    </div>
  );
}
